import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { connApi, spaceApi } from '../services/api';
import { useAnchorSpaceId } from './useAnchorSpaceId';
import type {
  CreateIntegrationInput,
  UpdateIntegrationInput,
  CreateRoutingRuleInput,
} from '../types/integrations';

// ---------------------------------------------------------------------------
// Org integrations
// ---------------------------------------------------------------------------
//
// Read paths (`list`, routing-rule CRUD), `delete`, and events hit the
// dedicated org routes that don't need a space anchor. `get` and `update`
// still go through the space-scoped CRUD route with an anchor spaceId
// resolved from the signed-in admin — see `useAnchorSpaceId`.

export function useOrgIntegrations(opts?: { provider?: string }) {
  return useQuery({
    queryKey: ['org', 'integrations', { provider: opts?.provider }],
    queryFn: () => connApi.org.integrations.list(opts),
  });
}

export function useOrgIntegration(integrationId: string | undefined) {
  const anchorSpaceId = useAnchorSpaceId();
  return useQuery({
    queryKey: ['org', 'integrations', integrationId],
    queryFn: () =>
      connApi.org.integrations.get(anchorSpaceId!, integrationId!),
    enabled: !!integrationId && !!anchorSpaceId,
  });
}

export function useOrgIntegrationEvents(integrationId: string | undefined) {
  return useQuery({
    queryKey: ['org', 'integrations', integrationId, 'events'],
    queryFn: () => connApi.org.integrations.listEvents(integrationId!),
    enabled: !!integrationId,
    refetchInterval: 30000,
  });
}

export function useOrgIntegrationEventDetail(
  integrationId: string | undefined,
  eventId: string | null,
) {
  return useQuery({
    queryKey: ['org', 'integrations', integrationId, 'events', eventId],
    queryFn: () =>
      connApi.org.integrations.getEvent(integrationId!, eventId!),
    enabled: !!integrationId && !!eventId,
    staleTime: Infinity,
  });
}

export function useCreateOrgIntegration() {
  const anchorSpaceId = useAnchorSpaceId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateIntegrationInput, 'scope'>) => {
      if (!anchorSpaceId) {
        throw new Error('No anchor space available — wait for spaces to load.');
      }
      return connApi.org.integrations.create(anchorSpaceId, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', 'integrations'] });
    },
  });
}

export function useUpdateOrgIntegration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      integrationId,
      input,
    }: {
      integrationId: string;
      input: UpdateIntegrationInput;
    }) => connApi.org.integrations.update(integrationId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['org', 'integrations'] });
      queryClient.invalidateQueries({
        queryKey: ['org', 'integrations', variables.integrationId],
      });
    },
  });
}

export function useDeleteOrgIntegration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (integrationId: string) =>
      connApi.org.integrations.delete(integrationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', 'integrations'] });
    },
  });
}

export function useOrgIntegrationRedeliver(integrationId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      connApi.org.integrations.redeliverEvent(integrationId!, eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['org', 'integrations', integrationId, 'events'],
      });
      queryClient.invalidateQueries({
        queryKey: ['org', 'integrations', integrationId],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Routing rules — direct org routes (no anchor needed).
// ---------------------------------------------------------------------------

export function useRoutingRules(integrationId: string | undefined) {
  return useQuery({
    queryKey: ['org', 'integrations', integrationId, 'rules'],
    queryFn: () => connApi.org.integrations.listRules(integrationId!),
    enabled: !!integrationId,
  });
}

export function useCreateRoutingRule(integrationId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRoutingRuleInput) =>
      connApi.org.integrations.createRule(integrationId!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['org', 'integrations', integrationId, 'rules'],
      });
      // Counters on the integration record may have shifted.
      queryClient.invalidateQueries({
        queryKey: ['org', 'integrations', integrationId],
      });
    },
  });
}

export function useDeleteRoutingRule(integrationId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) =>
      connApi.org.integrations.deleteRule(integrationId!, ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['org', 'integrations', integrationId, 'rules'],
      });
      queryClient.invalidateQueries({
        queryKey: ['org', 'integrations', integrationId],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Space-side discovery — relocated path.
// ---------------------------------------------------------------------------

export function useRoutedEventTypes(spaceId: string | undefined) {
  return useQuery({
    queryKey: ['space', spaceId, 'routed-event-types'],
    queryFn: () => spaceApi(spaceId!).integrations.listRoutedEventTypes(),
    enabled: !!spaceId,
  });
}
